import { Plus, RefreshCw } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import useTranslations from "@/shared/lang/useTranslations";

import CreateClientModal from "../components/CreateClientModal";
import { OrganizationClientsView } from "../components/OrganizationClientsView";
import { OrganizationsPortfolioView } from "../components/OrganizationsPortfolioView";

/**
 * `/app/clients` — master-detail root.
 *
 * Internal users land on the organizations portfolio (level 1) and drill down to
 * `/app/clients/organizations/:id`. Organization users see their own clients list directly (the backend
 * scopes it by the JWT organization).
 */
const ClientsPage = () => {
  const { t } = useTranslations("clients");
  const { t: tGlobal } = useTranslations("global");
  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_gestion") },
      { label: tGlobal("sidebar_menu_clients"), to: "/app/clients" },
    ],
    [tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);

  const { isInternalUser } = useAuth();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  const handleCreateSuccess = useCallback(() => {
    setRefreshToken((token) => token + 1);
  }, []);

  // The route-level `RequireAccess to="/app/clients"` already enforces `clients:read`. A second check
  // here could only disagree with it, and the one that used to live here ended in `return true`.

  return (
    <div className="space-y-6 p-2">
      <ColocaPageHeader
        title={t("page_title")}
        description={t("page_subtitle")}
        actions={
          <>
            <ColocaButton
              variant="default"
              icon={RefreshCw}
              onClick={handleCreateSuccess}
              aria-label={t("button_refresh")}
            >
              {t("button_refresh")}
            </ColocaButton>
            <ColocaButton variant="primary" icon={Plus} onClick={() => setCreateModalOpen(true)}>
              {isInternalUser ? t("button_create_new") : t("button_create_client")}
            </ColocaButton>
          </>
        }
      />

      {isInternalUser ? (
        <OrganizationsPortfolioView
          refreshToken={refreshToken}
          onCreateOrganization={() => setCreateModalOpen(true)}
        />
      ) : (
        <OrganizationClientsView
          showKpis
          refreshToken={refreshToken}
          onCreateClient={() => setCreateModalOpen(true)}
        />
      )}

      <CreateClientModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};

export { ClientsPage };
export default ClientsPage;
